import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Search,
  Download,
  Trash2,
  Eye,
  Pencil,
  X,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  FileDown,
  Inbox,
} from 'lucide-react';
import { api } from '../../utils/api';
import { useContacts } from '../../hooks/useContacts';
import { downloadVCard } from '../../utils/vcard';
import type { Contact } from '../../types';

/* ------------------------------------------------------------------ */
/*  常量                                                                */
/* ------------------------------------------------------------------ */

const PAGE_SIZE = 20;
const DEBOUNCE_MS = 300;

/* ------------------------------------------------------------------ */
/*  分页组件                                                            */
/* ------------------------------------------------------------------ */

interface PaginationProps {
  page: number;
  total: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({ page, total, pageSize, onPageChange }) => {
  const totalPages = Math.ceil(total / pageSize);

  if (totalPages <= 1) return null;

  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (page > 3) pages.push('...');
      for (let i = Math.max(2, page - 1); i <= Math.min(totalPages - 1, page + 1); i++) {
        pages.push(i);
      }
      if (page < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  return (
    <div className="flex items-center justify-between px-1">
      <span className="text-sm text-slate-500">
        共 <span className="font-medium text-slate-700">{total}</span> 条记录
      </span>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(page - 1)}
          disabled={page <= 1}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-4 h-4" />
          上一页
        </button>
        {getPageNumbers().map((p, idx) =>
          typeof p === 'string' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-slate-400 text-sm">
              ...
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                p === page
                  ? 'bg-blue-600 text-white'
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(page + 1)}
          disabled={page >= totalPages}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          下一页
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  编辑弹窗                                                            */
/* ------------------------------------------------------------------ */

interface EditModalProps {
  contact: Contact | null;
  onClose: () => void;
  onSave: (contact: Contact) => void;
}

const EditModal: React.FC<EditModalProps> = ({ contact, onClose, onSave }) => {
  const [form, setForm] = useState<Partial<Contact>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (contact) {
      setForm({ ...contact });
      setError('');
    }
  }, [contact]);

  if (!contact) return null;

  const handleChange = (field: keyof Contact, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.name?.trim()) {
      setError('姓名不能为空');
      return;
    }
    if (!form.phone?.trim()) {
      setError('电话不能为空');
      return;
    }

    setSaving(true);
    setError('');

    try {
      await api.put<Contact>(`/api/contacts/${contact.id}`, form);
      onSave({ ...contact, ...form });
    } catch (err) {
      setError(err instanceof Error ? err.message : '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const fields: { key: keyof Contact; label: string; type?: string }[] = [
    { key: 'name', label: '姓名' },
    { key: 'phone', label: '电话' },
    { key: 'phone2', label: '备用电话' },
    { key: 'email', label: '邮箱', type: 'email' },
    { key: 'id_card', label: '身份证号' },
    { key: 'vehicle_brand', label: '车辆品牌' },
    { key: 'vehicle_series', label: '车系' },
    { key: 'vehicle_model', label: '车型' },
    { key: 'purchase_date', label: '购车时间', type: 'date' },
    { key: 'company', label: '公司' },
    { key: 'address', label: '地址' },
    { key: 'remark', label: '备注' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">编辑联系人</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 表单内容 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-sm text-red-600">
              <AlertCircle className="w-4 h-4 shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {fields.map(({ key, label, type }) => (
              <div key={key} className={key === 'address' || key === 'remark' ? 'sm:col-span-2' : ''}>
                <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
                {key === 'remark' ? (
                  <textarea
                    value={(form[key] as string) || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    rows={3}
                    className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors resize-none"
                    placeholder={`请输入${label}`}
                  />
                ) : (
                  <input
                    type={type || 'text'}
                    value={(form[key] as string) || ''}
                    onChange={(e) => handleChange(key, e.target.value)}
                    className="block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
                    placeholder={`请输入${label}`}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* 底部按钮 */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            取消
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-4 py-2 rounded-lg bg-blue-600 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  查看详情弹窗                                                        */
/* ------------------------------------------------------------------ */

interface DetailModalProps {
  contact: Contact | null;
  onClose: () => void;
}

const DetailModal: React.FC<DetailModalProps> = ({ contact, onClose }) => {
  if (!contact) return null;

  const infoItems: { label: string; value?: string }[] = [
    { label: '姓名', value: contact.name },
    { label: '电话', value: contact.phone },
    { label: '备用电话', value: contact.phone2 },
    { label: '邮箱', value: contact.email },
    { label: '身份证号', value: contact.id_card },
    { label: '车辆品牌', value: contact.vehicle_brand },
    { label: '车系', value: contact.vehicle_series },
    { label: '车型', value: contact.vehicle_model },
    { label: '购车时间', value: contact.purchase_date },
    { label: '公司', value: contact.company },
    { label: '地址', value: contact.address },
    { label: '备注', value: contact.remark },
    { label: '来源', value: contact.source === 'manual' ? '手动添加' : '文件导入' },
    { label: '创建时间', value: new Date(contact.created_at).toLocaleString('zh-CN') },
    { label: '更新时间', value: new Date(contact.updated_at).toLocaleString('zh-CN') },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col">
        {/* 头部 */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">联系人详情</h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* 详情内容 */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="space-y-3">
            {infoItems.map(({ label, value }) => (
              <div key={label} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
                <span className="text-sm text-slate-400 w-20 shrink-0 text-right">{label}</span>
                <span className="text-sm text-slate-800 font-medium">{value || '-'}</span>
              </div>
            ))}
          </div>
        </div>

        {/* 底部 */}
        <div className="flex items-center justify-end px-6 py-4 border-t border-slate-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors"
          >
            关闭
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  ContactsPage 主组件                                                 */
/* ------------------------------------------------------------------ */

const ContactsPage: React.FC = () => {
  // 搜索和筛选状态
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [source, setSource] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [page, setPage] = useState(1);

  // 选中状态
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // 弹窗状态
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [viewingContact, setViewingContact] = useState<Contact | null>(null);

  // 操作状态
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');

  // debounce timer
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 搜索 debounce
  useEffect(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, DEBOUNCE_MS);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [searchInput]);

  // 获取联系人数据
  const { contacts, total, loading, refetch } = useContacts({
    page,
    pageSize: PAGE_SIZE,
    search,
    source,
    sortBy,
    sortOrder: 'desc',
  });

  // 全选 / 取消全选
  const allSelected = contacts.length > 0 && contacts.every((c) => selectedIds.has(c.id));
  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(contacts.map((c) => c.id)));
    }
  };
  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // 删除单个联系人
  const handleDelete = async (id: string) => {
    if (!window.confirm('确定要删除该联系人吗？此操作不可撤销。')) return;
    setActionLoading(true);
    setActionError('');
    try {
      await api.del('/api/contacts', { ids: [id] });
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '删除失败');
    } finally {
      setActionLoading(false);
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`确定要删除选中的 ${ids.length} 个联系人吗？此操作不可撤销。`)) return;
    setActionLoading(true);
    setActionError('');
    try {
      await api.del('/api/contacts', { ids });
      setSelectedIds(new Set());
      refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '批量删除失败');
    } finally {
      setActionLoading(false);
    }
  };

  // 导出选中
  const handleExportSelected = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setActionLoading(true);
    setActionError('');
    try {
      const result = await api.post<Blob>('/api/export', { ids });
      const blob = result instanceof Blob ? result : new Blob([JSON.stringify(result)], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_selected_${Date.now()}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setActionLoading(false);
    }
  };

  // 导出全部
  const handleExportAll = async () => {
    setActionLoading(true);
    setActionError('');
    try {
      const result = await api.post<Blob>('/api/export', { all: true });
      const blob = result instanceof Blob ? result : new Blob([JSON.stringify(result)], { type: 'text/vcard' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `contacts_all_${Date.now()}.vcf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err instanceof Error ? err.message : '导出失败');
    } finally {
      setActionLoading(false);
    }
  };

  // 编辑保存回调
  const handleEditSave = () => {
    setEditingContact(null);
    refetch();
  };

  // 格式化日期
  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

  return (
    <div className="space-y-4">
      {/* 错误提示 */}
      {actionError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="w-4 h-4 shrink-0" />
          {actionError}
          <button onClick={() => setActionError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* 搜索和筛选栏 */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-col lg:flex-row gap-3">
          {/* 搜索框 */}
          <div className="relative flex-1">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
              <Search className="w-4 h-4 text-slate-400" />
            </div>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="搜索姓名或电话..."
              className="block w-full rounded-lg border border-slate-300 bg-white py-2 pl-10 pr-3 text-sm text-slate-800 placeholder:text-slate-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
            />
          </div>

          {/* 来源筛选 */}
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            <option value="">全部来源</option>
            <option value="manual">手动添加</option>
            <option value="import">文件导入</option>
          </select>

          {/* 排序 */}
          <select
            value={sortBy}
            onChange={(e) => {
              setSortBy(e.target.value);
              setPage(1);
            }}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
          >
            <option value="created_at">按创建时间</option>
            <option value="name">按姓名</option>
            <option value="phone">按电话</option>
          </select>

          {/* 操作按钮 */}
          <div className="flex items-center gap-2">
            <button
              onClick={handleExportSelected}
              disabled={selectedIds.size === 0 || actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <FileDown className="w-4 h-4" />
              导出选中
            </button>
            <button
              onClick={handleExportAll}
              disabled={actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-blue-200 bg-blue-50 text-sm font-medium text-blue-600 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Download className="w-4 h-4" />
              导出全部
            </button>
            <button
              onClick={handleBatchDelete}
              disabled={selectedIds.size === 0 || actionLoading}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 bg-red-50 text-sm font-medium text-red-600 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              删除选中
            </button>
          </div>
        </div>
      </div>

      {/* 数据表格 */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="py-3 px-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">姓名</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">电话</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">车辆品牌</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">车系</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">车型</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">购车时间</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">来源</th>
                <th className="py-3 px-3 text-left font-medium text-slate-500">创建时间</th>
                <th className="py-3 px-3 text-center font-medium text-slate-500">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    {Array.from({ length: 10 }).map((_, j) => (
                      <td key={j} className="py-3 px-3">
                        <div className="h-4 bg-slate-200 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : contacts.length === 0 ? (
                <tr>
                  <td colSpan={10} className="py-16 text-center">
                    <Inbox className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-400 text-sm">暂无联系人数据</p>
                  </td>
                </tr>
              ) : (
                contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={`border-b border-slate-50 hover:bg-slate-50 transition-colors ${
                      selectedIds.has(contact.id) ? 'bg-blue-50/50' : ''
                    }`}
                  >
                    <td className="py-3 px-3">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    <td className="py-3 px-3 text-slate-800 font-medium">{contact.name}</td>
                    <td className="py-3 px-3 text-slate-600">{contact.phone}</td>
                    <td className="py-3 px-3 text-slate-600">{contact.vehicle_brand || '-'}</td>
                    <td className="py-3 px-3 text-slate-600">{contact.vehicle_series || '-'}</td>
                    <td className="py-3 px-3 text-slate-600">{contact.vehicle_model || '-'}</td>
                    <td className="py-3 px-3 text-slate-600">{contact.purchase_date || '-'}</td>
                    <td className="py-3 px-3">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          contact.source === 'manual'
                            ? 'bg-blue-50 text-blue-700'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {contact.source === 'manual' ? '手动添加' : '文件导入'}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-slate-400 text-xs whitespace-nowrap">
                      {formatDate(contact.created_at)}
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => setViewingContact(contact)}
                          title="查看详情"
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-blue-600 transition-colors"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setEditingContact(contact)}
                          title="编辑"
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-amber-600 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(contact.id)}
                          disabled={actionLoading}
                          title="删除"
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors disabled:opacity-40"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* 分页 */}
        <div className="px-4 py-3 border-t border-slate-200">
          <Pagination
            page={page}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      </div>

      {/* 编辑弹窗 */}
      {editingContact && (
        <EditModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={handleEditSave}
        />
      )}

      {/* 查看详情弹窗 */}
      {viewingContact && (
        <DetailModal
          contact={viewingContact}
          onClose={() => setViewingContact(null)}
        />
      )}
    </div>
  );
};

export default ContactsPage;
